import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast as globalToast } from '@/hooks/use-toast';
import { type CallBackData, csvBoxSingleton } from '@/lib/csv-box';
import {
  type ToastUpdate,
  bindDealItemsInProgressToastToDatasource,
  clearDealItemsInProgressToast,
  registerDealItemsInProgressToast,
} from '@/lib/deal-items-toast';
import {
  DatasourceInstructionMode,
  DealItemAnchorKey,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { useEditPricingMode } from '@/lib/hooks/useEditPricingMode';
import { useUploadCsv } from '@/lib/hooks/useUploadCsv';
import { detectEmerchColumns } from '@/lib/mapping';
import { trackEvent } from '@/lib/mixpanel';
import {
  dynamicColumnsForPricingMode,
  csvImportModeLabel,
  dropDealItemIdColumn,
  flattenDynamicData,
  isDealItemIdHeader,
  mergeUnmappedColumns,
  normalizeDealItemIdColumn,
  sanitizeFilename,
} from '@/lib/utils/csv-import';
import { getDomainErrorMessage } from '@/lib/utils/domain-error-translator';
import { createCsvFileFromImportData } from '@/lib/utils/import-data-to-csv';
import { pricingModeState } from '@/states/pricing-mode.state';
import { EmerchConsentRequest } from '@/types/emerch-consent';
import {
  type PricingModeChoice,
  PricingModeRequest,
} from '@/types/pricing-mode-request';
import { useAtomValue } from 'jotai';

import { useUser } from '../hooks/use-user';

type CsvBoxImportButtonProps = {
  dealId: string;
  onUploadSuccess?: () => Promise<void>;
  onClick?: () => void;
  onCloseModal?: () => void;
  autoOpen?: boolean;
  hidden?: boolean;
  onEmerchConsentRequested?: (request: EmerchConsentRequest) => void;
  /**
   * Demande les colonnes de prix à inclure, affichées comme étape du wizard
   * avant l'ouverture de CSVBox. Les choix dépendent du profil et du mode de
   * prix actuel de l'offre.
   */
  onPricingModeRequested?: (request: PricingModeRequest) => void;
  /** Mode d'import envoyé au backend, cf. `CsvBoxImportMode`. */
  importMode: CsvBoxImportMode;
};

/**
 * - `legacy` : flux de production, « ajouter » (`CREATE_ONLY`) ou « mettre à
 *   jour » (`UPDATE_ONLY`) avec sa clé de réconciliation. Rien n'est supprimé.
 * - `sync` : synchronisation par ID deal item (`SYNC_WITH_DEAL_ITEM_ID`),
 *   ouverte par le clic simple aux internes Choose (Option + clic pour le
 *   legacy) : le fichier est la vérité de la version active, items absents
 *   supprimés.
 */
export type CsvBoxImportMode =
  | { kind: 'sync' }
  | {
      kind: 'legacy';
      mode: 'add' | 'update';
      anchorKey: 'auto' | 'url' | 'sku';
    };

const legacyInstructionMode = (mode: 'add' | 'update') =>
  mode === 'add'
    ? DatasourceInstructionMode.CreateOnly
    : DatasourceInstructionMode.UpdateOnly;

const legacyAnchorKey = (anchorKey: 'auto' | 'url' | 'sku') => {
  if (anchorKey === 'url') return DealItemAnchorKey.Url;
  if (anchorKey === 'sku') return DealItemAnchorKey.Sku;
  return null;
};

export function CsvBoxImportButton({
  dealId,
  onClick,
  onUploadSuccess,
  onCloseModal,
  autoOpen,
  hidden,
  onEmerchConsentRequested,
  onPricingModeRequested,
  importMode,
}: CsvBoxImportButtonProps) {
  const { t, i18n } = useTranslation();

  const user = useUser();
  const isInternalUser = user?.isInternalUser ?? false;
  const pricingMode = useAtomValue(pricingModeState);
  const { handlePricingModeChange } = useEditPricingMode(dealId);
  // La synchronisation ne matche que sur l'ID deal item (colonne CSVBox), sans
  // clé d'ancrage ; le flow legacy garde son mode et sa clé.
  const legacyMode = importMode.kind === 'legacy' ? importMode : undefined;
  const graphqlInstructionMode = legacyMode
    ? legacyInstructionMode(legacyMode.mode)
    : DatasourceInstructionMode.SyncWithDealItemId;
  const instructionMode = csvImportModeLabel(graphqlInstructionMode);

  const { addCsvDataSourceToDeal } = useUploadCsv(
    dealId,
    graphqlInstructionMode,
    legacyMode ? legacyAnchorKey(legacyMode.anchorKey) : undefined
  );

  const handleCsvBoxUpload = useCallback(
    async (data: CallBackData) => {
      const filename = sanitizeFilename(data.original_filename);
      let toastTrackerId: string | undefined;

      // Time the rep spent in the CSVBox importer (open → completion callback)
      const importerOpenedAt = importerOpenedAtRef.current;
      importerOpenedAtRef.current = null;
      if (importerOpenedAt !== null) {
        trackEvent('IMPORT_CSV_UPLOADED', {
          dealId,
          instructionMode,
          durationMs: Math.round(performance.now() - importerOpenedAt),
          rowCount: data.row_count,
          rowSuccess: data.row_success,
          rowFail: data.row_fail,
        });
      }

      try {
        if (!data.rows || data.rows.length === 0) {
          throw new Error(
            "Aucune donnée reçue. Activez l'option « Send JSON data to client » dans le Destination du sheet CSVBox."
          );
        }

        // Aplatit les colonnes dynamiques (_dynamic_data) au niveau racine pour
        // qu'elles soient incluses dans le CSV envoyé au backend (signed URL) :
        // le nom de colonne canonique suffit au mapping, pas besoin de les
        // injecter en variable de mutation.
        // La colonne ID deal item n'est lue que si elle est mappée dans
        // CSVBox (renommée au besoin sous l'en-tête que le backend matche).
        // Non mappée, elle reste dans _unmapped_data et n'est pas envoyée.
        // Elle n'est jamais proposée comme colonne E-Merch. En legacy, elle
        // est retirée : le backend la rangerait sinon en E-Merch.
        const flatRows = flattenDynamicData(data.rows);
        const importRows =
          instructionMode === 'sync'
            ? normalizeDealItemIdColumn(flatRows)
            : dropDealItemIdColumn(flatRows);

        // Detect emerch columns before upload to ask for consent
        const emerchColumns = detectEmerchColumns(
          data.raw_columns?.filter((column) => !isDealItemIdHeader(column)),
          importRows
        );
        let eMerchCustomFieldsHeaders: string[] | undefined;

        if (emerchColumns.length > 0 && onEmerchConsentRequested) {
          const selectedColumns = await new Promise<string[] | null>(
            (resolve) => {
              onEmerchConsentRequested({
                dealId,
                columns: emerchColumns,
                onDecision: resolve,
              });
            }
          );

          if (selectedColumns && selectedColumns.length > 0) {
            eMerchCustomFieldsHeaders = selectedColumns;
          }
        }

        const { update, dismiss } = globalToast({
          description: `⏳ ${t('general.csv_import_in_progress')}`,
          duration: Number.POSITIVE_INFINITY,
        });
        toastTrackerId = registerDealItemsInProgressToast(
          update as ToastUpdate,
          dismiss
        );

        const rows = mergeUnmappedColumns(
          importRows,
          eMerchCustomFieldsHeaders ?? []
        );

        // Persiste le mode de calcul choisi (modale) juste avant l'upload.
        const pendingPricingMode = pendingPricingModeRef.current;
        pendingPricingModeRef.current = null;
        if (
          isInternalUser &&
          pendingPricingMode &&
          pendingPricingMode !== pricingMode
        ) {
          await handlePricingModeChange(pendingPricingMode);
        }

        const file = createCsvFileFromImportData(rows, filename);
        const result = await addCsvDataSourceToDeal(
          file,
          eMerchCustomFieldsHeaders,
          String(data.import_id)
        );

        if (!result.success) {
          clearDealItemsInProgressToast({ trackerId: toastTrackerId });
          const errorMessage = getDomainErrorMessage(
            result.domainError.code,
            t,
            'general.error_import_csv_message'
          );
          const errorTitle =
            result.domainError.code !== 'IMPORT_FAILED'
              ? t('general.error_import_csv')
              : t('general.error_import');
          globalToast({
            title: errorTitle,
            description: errorMessage,
            variant: 'destructive',
          });
          return;
        }

        bindDealItemsInProgressToastToDatasource(toastTrackerId, result.id);

        await onUploadSuccess?.();
      } catch (error) {
        if (toastTrackerId) {
          clearDealItemsInProgressToast({ trackerId: toastTrackerId });
        }
        globalToast({
          title: t('general.error_import'),
          description:
            error instanceof Error ? error.message : t('general.error_unknown'),
          variant: 'destructive',
        });
      }
    },
    [
      addCsvDataSourceToDeal,
      dealId,
      handlePricingModeChange,
      isInternalUser,
      instructionMode,
      onEmerchConsentRequested,
      onUploadSuccess,
      pricingMode,
      t,
    ]
  );

  const handleCsvBoxComplete = useCallback(
    (result: boolean, data: CallBackData | null) => {
      // Any import callback (success or failure) means the rep engaged with the
      // import — the upcoming onClose must not be counted as an abandonment.
      importCompletedRef.current = true;
      if (result && data) {
        handleCsvBoxUpload(data);
      }
    },
    [handleCsvBoxUpload]
  );

  // Fired by CSVBox when the modal closes. If no import happened during this
  // session, the rep left the importer → track the abandonment with the time
  // spent, to compare time-to-complete vs time-to-abandon.
  const handleCsvBoxClose = useCallback(() => {
    const importerOpenedAt = importerOpenedAtRef.current;
    importerOpenedAtRef.current = null;
    if (!importCompletedRef.current && importerOpenedAt !== null) {
      trackEvent('IMPORT_CSV_IMPORTER_ABANDONED', {
        dealId,
        instructionMode,
        durationMs: Math.round(performance.now() - importerOpenedAt),
      });
    }
    // Session over: drop the import subscription if it never fired (abandon),
    // since this component may already be unmounted (the modal outlives it).
    pendingUnsubscribeRef.current?.();
    pendingUnsubscribeRef.current = null;
    pendingCloseUnsubscribeRef.current = null;
  }, [dealId, instructionMode]);

  const pendingUnsubscribeRef = useRef<null | (() => void)>(null);
  const pendingCloseUnsubscribeRef = useRef<null | (() => void)>(null);
  // Timestamp (performance.now) of when the CSVBox importer modal was opened,
  // used to measure how long the rep spends preparing the CSV.
  const importerOpenedAtRef = useRef<number | null>(null);
  // Whether an import callback fired during the current modal session (used to
  // distinguish a completed import from an abandonment on close).
  const importCompletedRef = useRef(false);
  // Mode de calcul choisi dans la modale (au clic), à persister juste avant
  // l'upload — pas avant, pour ne rien changer si la sale abandonne.
  const pendingPricingModeRef = useRef<PricingMode | null>(null);

  const openCsvBox = (modeForColumns?: PricingMode) => {
    const language = i18n.language as 'fr' | 'en';
    // Repart d'un importer propre : CSVBox cumule les colonnes dynamiques sur une
    // même instance, donc sans reset un mode précédent « fuiterait » ses colonnes
    // prix sur l'ouverture suivante (ex. rétrocession en mode Marge fixe).
    csvBoxSingleton.reset();
    csvBoxSingleton.setOptions({ language });
    csvBoxSingleton.setUser({
      user_id: user?.providerUser.sub ?? 'unauthenticated',
      instruction_mode: instructionMode,
      ...(legacyMode && { anchor_key: legacyMode.anchorKey }),
      deal_id: dealId,
    });
    // Colonnes dynamiques pour CETTE ouverture : les colonnes prix du mode
    // choisi (la colonne « Deal Item Id » est dans le sheet CSVBox). On
    // (re)définit TOUJOURS, même sans colonne prix (skip), pour ne rien
    // hériter d'avant.
    csvBoxSingleton.setDynamicColumns(
      modeForColumns ? dynamicColumnsForPricingMode(modeForColumns) : []
    );
    // S'abonner une fois juste avant l'ouverture du modal
    pendingUnsubscribeRef.current?.();
    pendingCloseUnsubscribeRef.current?.();
    pendingUnsubscribeRef.current =
      csvBoxSingleton.subscribeOnce(handleCsvBoxComplete);
    pendingCloseUnsubscribeRef.current =
      csvBoxSingleton.subscribeOnceClose(handleCsvBoxClose);
    importCompletedRef.current = false;
    importerOpenedAtRef.current = performance.now();
    trackEvent('IMPORT_CSV_IMPORTER_OPENED', { dealId, instructionMode });
    csvBoxSingleton.openModal();
    onCloseModal?.();
  };

  const handleClick = async () => {
    onClick?.();

    // Flux produits : choix de la base de saisie des prix AVANT d'ouvrir CSVBox.
    // C'est une ÉTAPE du wizard (même Dialog d'import), dont le wording/les
    // options s'adaptent au profil (interne Choose vs marque) — cf.
    // ViewProductsPricingMode. On NE ferme donc PAS la Dialog ici : elle reste
    // ouverte pour afficher l'étape, et sera fermée par openCsvBox (onCloseModal)
    // une fois le choix confirmé et CSVBox ouvert.
    if (onPricingModeRequested) {
      const choice = await new Promise<PricingModeChoice | null>((resolve) => {
        onPricingModeRequested({
          dealId,
          initialMode: pricingMode,
          onDecision: resolve,
        });
      });

      // Annulation → on n'ouvre pas l'import.
      if (choice === null) {
        return;
      }

      // Une marque ne peut jamais modifier le mode de prix de l'offre. Si
      // l'offre est pilotée par la marge, l'import s'ouvre nécessairement sans
      // colonnes prix, même si un appelant renvoie un ancien choix de cession.
      if (!isInternalUser && pricingMode === PricingMode.MarginFixed) {
        pendingPricingModeRef.current = null;
        openCsvBox();
        return;
      }

      // « Je ne mets pas de prix à jour » : ni mutation, ni colonnes prix
      // dynamiques, on ouvre simplement CSVBox.
      if (choice === 'skip') {
        pendingPricingModeRef.current = null;
        openCsvBox();
        return;
      }

      // On NE persiste pas tout de suite : la mutation updateDealPricingMode
      // est faite juste avant l'upload (cf. handleCsvBoxUpload), pour ne pas
      // modifier le deal si la sale abandonne l'import.
      pendingPricingModeRef.current = choice;
      openCsvBox(choice);
      return;
    }

    pendingPricingModeRef.current = null;
    openCsvBox();
  };

  // Auto open when required (only when autoOpen toggles, not when handleClick
  // identity changes). The ref keeps React.StrictMode's dev double-run of this
  // effect from resetting CSVBox and tracking the importer opening twice.
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (!autoOpen) {
      hasAutoOpenedRef.current = false;
      return;
    }
    if (hasAutoOpenedRef.current) {
      return;
    }
    hasAutoOpenedRef.current = true;
    handleClick();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only when autoOpen changes
  }, [autoOpen]);

  // NOTE: we intentionally do NOT unsubscribe from the CSVBox singleton on
  // unmount. This component is designed to unmount right after opening the
  // modal (e.g. autoOpen+hidden in view-products-menu calls onCloseModal which
  // removes it from the tree), while the CSVBox modal lives on. Unsubscribing
  // on unmount would drop the completion/close handlers before the user
  // finishes, so IMPORT_CSV_UPLOADED / IMPORT_CSV_IMPORTER_ABANDONED (and the
  // upload itself) would never fire. The handlers use refs + module-level
  // trackEvent (no setState), and subscriptions are cleaned up per session:
  // subscribeOnce self-removes on completion, and handleCsvBoxClose drops the
  // import subscription on close.

  if (hidden) {
    return null;
  }

  return (
    <button
      className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
      onClick={handleClick}
    >
      {t('general.next')}
    </button>
  );
}
